import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Select,
  Button,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Checkbox,
  Image,
  Badge,
  Heading,
  Skeleton,
  Stack,
  useToast,
  Icon,
  Tooltip,
  Grid,
} from "@chakra-ui/react";

import { FaRegUser, FaUserTie } from "react-icons/fa6";
import { MdAirlineSeatReclineNormal } from "react-icons/md";
import { FiTrash2, FiPlus, FiArrowRight } from "react-icons/fi";
import { useState, useEffect, useRef, useMemo, Fragment } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "../../configs/axios.config";
import InputSuggestionWithExposeRef from "../../components/InputSuggestionWithExposeRef";
import BreadCrumbMaker from "../../components/BreadCrumbMaker";
import DateInput from "../../components/PickupDate";
import PickupTime from "../../components/PickupTime";

// Parse DD/MM/YYYY format to Date object
const parseDDMMYYYY = (dateStr) => {
  if (!dateStr) return null;
  const [day, month, year] = dateStr.split("/").map(Number);
  return new Date(year, month - 1, day);
};

// Format Date object to DD/MM/YYYY string
const formatToDDMMYYYY = (date) => {
  if (!date) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const calculateChargedTripDays = (
  pickupDateStr,
  returnDateStr,
  pickupTimeStr,
  pickupDayGraceHours = 3,
) => {
  if (!pickupDateStr || !returnDateStr) return 1;

  const pickupDate = parseDDMMYYYY(pickupDateStr);
  const returnDate = parseDDMMYYYY(returnDateStr);

  if (
    !pickupDate ||
    !returnDate ||
    Number.isNaN(pickupDate.getTime()) ||
    Number.isNaN(returnDate.getTime())
  ) {
    return 1;
  }

  const millisecondsInDay = 1000 * 60 * 60 * 24;
  const calendarDaysBetween = Math.ceil(
    (returnDate - pickupDate) / millisecondsInDay,
  );

  const normalizedPickupTime = pickupTimeStr || "00:00";
  const [pickupHour, pickupMinute] = normalizedPickupTime
    .split(":")
    .map(Number);
  const pickupHourInDay =
    (Number.isFinite(pickupHour) ? pickupHour : 0) +
    (Number.isFinite(pickupMinute) ? pickupMinute : 0) / 60;
  const remainingHoursInPickupDay = 24 - pickupHourInDay;
  const shouldChargePickupDay = remainingHoursInPickupDay > pickupDayGraceHours;

  return Math.max(calendarDaysBetween + (shouldChargePickupDay ? 1 : 0), 1);
};

const normalizeValue = (value) => String(value || "").trim().toLowerCase();

const Results = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fromRef = useRef();
  const toRef = useRef();

  const initialFormData = {
    tripType: location.state?.tripType || "OW",
    airportTripType: location.state?.airportTripType || "",
    from: location.state?.from || "",
    to: location.state?.to || "",
    stops: location.state?.stops || [],
    pickupdate: location.state?.pickupdate || "",
    pickuptime: location.state?.pickuptime || "",
    returndate: location.state?.returndate || "",
    distance: location.state?.distance || "",
    hours:
      location.state?.hours || (location.state?.tripType === "LR" ? 4 : null),
  };

  const [formData, setFormData] = useState(initialFormData);
  const [price, setPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(10000);
  const [carTypeFilter, setCarTypeFilter] = useState("");
  const [premiumServices, setPremiumServices] = useState(false);
  // const [applySurcharges, setApplySurcharges] = useState(false);
  // const [applyDiscount, setApplyDiscount] = useState(false);
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [distanceData, setDistanceData] = useState(null);
  // const [carPrices, setCarPrices] = useState({});
  const [availableHours, setAvailableHours] = useState([]);

  const [backendFareMap, setBackendFareMap] = useState({});
  const [isEditing, setIsEditing] = useState(false);

  const lastFarePayloadRef = useRef(null);

  // Update formData when location.state changes
  useEffect(() => {
    if (location.state) {
      setFormData({
        tripType: location.state?.tripType || "OW",
        airportTripType: location.state?.airportTripType || "",
        from: location.state?.from || "",
        to: location.state?.to || "",
        stops: location.state?.stops || [],
        pickupdate: location.state?.pickupdate || "",
        pickuptime: location.state?.pickuptime || "",
        returndate: location.state?.returndate || "",
        distance: location.state?.distance || "",
        hours:
          location.state?.hours ||
          (location.state?.tripType === "LR" ? 4 : null),
      });
    }
  }, [location.state]);

  // Auto-save formData to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("searchFormData", JSON.stringify(formData));
  }, [formData]);

  // Helper function to calculate tax from vehicle
  // pricing & tax now handled by backend
  // const calculateTax = (car, distanceInKm) => {
  //   const tripType = formData.tripType;
  //   let tax = 0;

  //   // Get pricing info based on trip type
  //   let pricingInfo = null;
  //   if (tripType === "OW" || tripType === "RT") {
  //     pricingInfo = car.pricing?.outstationOneWay;
  //   } else if (tripType === "AT") {
  //     pricingInfo = car.pricing?.airportTransfer;
  //   } else if (tripType === "LR") {
  //     pricingInfo = car.pricing?.localRental?.[0];
  //   }

  //   console.log(`[${car.segmentName}] Pricing Info:`, pricingInfo);
  //   console.log(`[${car.segmentName}] Distance: ${distanceInKm} km`);

  //   // Get toll tax from vehicle pricing
  //   const tollTax = (pricingInfo?.tollCharge && distanceInKm) ? pricingInfo.tollCharge * distanceInKm : 0;

  //   // Get night charge from vehicle pricing
  //   const nightCharge = pricingInfo?.nightCharge || 0;

  //   // Get waiting charge from vehicle pricing
  //   const waitingCharge = pricingInfo?.waitingCharge || 0;

  //   console.log(`[${car.segmentName}] Toll Tax: ₹${pricingInfo?.tollCharge}/km × ${distanceInKm}km = ₹${tollTax}`);
  //   console.log(`[${car.segmentName}] Night Charge: ₹${nightCharge}`);
  //   console.log(`[${car.segmentName}] Waiting Charge: ₹${waitingCharge}`);

  //   // Return only toll tax
  //   console.log(`[${car.segmentName}] Displaying Toll Tax Only: ₹${tollTax}`);

  //   return Math.round(tollTax);
  // };

  // Helper function to get extra km rate
  const toNumberOrNull = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };

  const getSelectedRentalPackage = (car) => {
    const tripType = formData.tripType;
    if (tripType !== "LR" && tripType !== "HR") return null;

    const selectedHours = toNumberOrNull(formData.hours);
    const selectedKm = toNumberOrNull(
      formData.distance ?? distanceData?.distance,
    );

    if (!selectedHours) return null;

    const rentalType = tripType === "LR" ? "localRental" : "hourlyRental";
    const rentalOptions = car.pricing?.[rentalType] || [];

    return (
      rentalOptions.find((pkg) => {
        const pkgHours = toNumberOrNull(pkg.durationHours);
        const pkgKm = toNumberOrNull(pkg.includedKm);

        if (pkgHours !== selectedHours) return false;
        if (selectedKm == null) return true;

        return pkgKm === selectedKm;
      }) || null
    );
  };

  const getExtraKmRate = (car) => {
    const matchedPackage = getSelectedRentalPackage(car);
    return matchedPackage?.extraKmRate || 0;
  };

  // Helper function to get extra hour rate
  const getExtraHourRate = (car) => {
    const matchedPackage = getSelectedRentalPackage(car);
    return matchedPackage?.extraHourRate || 0;
  };

  // Helper function to calculate car price
  // const calculateCarPrice = (car, distanceInKm, includeSurcharges = false, includeDiscount = false) => {
  //   const tripType = formData.tripType;
  //   let basePrice = 0;

  //   if (tripType === "LR") {
  //     // Get the price based on selected hours
  //     const selectedHours = formData.hours ? parseInt(formData.hours) : null;
  //     const localRentalOptions = car.pricing?.localRental || [];

  //     if (selectedHours && localRentalOptions.length > 0) {
  //       // Find the package that matches the selected hours using durationHours
  //       const matchedPackage = localRentalOptions.find(pkg => pkg.durationHours === selectedHours);
  //       basePrice = matchedPackage ? matchedPackage.price : 0;
  //       console.log(`[${car.segmentName}] LR - Selected ${selectedHours} hours, Matched Package:`, matchedPackage, `Price: ₹${basePrice}`);
  //     } else {
  //       // If no hours selected, show the minimum price
  //       const localPrices = localRentalOptions.map((r) => r.price) || [0];
  //       basePrice = localPrices.length > 0 ? Math.min(...localPrices) : 0;
  //     }
  //   } else if (tripType === "HR") {
  //     // Get the price based on selected hours
  //     const selectedHours = formData.hours ? parseInt(formData.hours) : null;
  //     const hourlyRentalOptions = car.pricing?.hourlyRental || [];

  //     if (selectedHours && hourlyRentalOptions.length > 0) {
  //       // Find the package that matches the selected hours using durationHours
  //       const matchedPackage = hourlyRentalOptions.find(pkg => pkg.durationHours === selectedHours);
  //       basePrice = matchedPackage ? matchedPackage.price : 0;
  //       console.log(`[${car.segmentName}] HR - Selected ${selectedHours} hours, Matched Package:`, matchedPackage, `Price: ₹${basePrice}`);
  //     } else {
  //       // If no hours selected, show the minimum price
  //       const hourlyPrices = hourlyRentalOptions.map((r) => r.price) || [0];
  //       basePrice = hourlyPrices.length > 0 ? Math.min(...hourlyPrices) : 0;
  //     }
  //   } else if (tripType === "OW" || tripType === "RT") {
  //     const base = car.pricing?.outstationOneWay?.baseFare || 0;
  //     const perKm = car.pricing?.outstationOneWay?.perKmRate || 0;
  //     const driver = car.pricing?.outstationOneWay?.driverCharge || 0;
  //     const driverAllowance = car.pricing?.outstationOneWay?.driverAllowance || 0;

  //     basePrice = base + (perKm * distanceInKm) + driver;

  //     // For round trips with multiple days, add driver allowance per day
  //     if (tripType === "RT" && formData.pickupdate && formData.returndate) {
  //       const pickupDate = parseDDMMYYYY(formData.pickupdate);
  //       const returnDate = parseDDMMYYYY(formData.returndate);
  //       const daysDiff = Math.ceil((returnDate - pickupDate) / (1000 * 60 * 60 * 24));

  //       // Add driver allowance for additional days (first day is covered in base)
  //       if (daysDiff > 1) {
  //         basePrice += driverAllowance * (daysDiff - 1);
  //         console.log(`Round trip ${daysDiff} days: Added driver allowance ₹${driverAllowance * (daysDiff - 1)}`);
  //       }
  //     }
  //   } else if (tripType === "AT") {
  //     const base = car.pricing?.airportTransfer?.baseFare || 0;
  //     const perKm = car.pricing?.airportTransfer?.perKmRate || 0;
  //     const driver = car.pricing?.airportTransfer?.driverCharge || 0;
  //     basePrice = base + (perKm * distanceInKm) + driver;
  //   }

  //   // Apply discount from vehicle data if checkbox is checked
  //   if (includeDiscount) {
  //     const discount = car.discount?.discount || 0;
  //     if (discount > 0) {
  //       basePrice = basePrice - (basePrice * discount / 100);
  //     }
  //   }

  //   // Apply surcharge from vehicle data if checkbox is checked
  //   if (includeSurcharges) {
  //     const surcharge = car.surCharges?.surCharge || 0;
  //     if (surcharge > 0) {
  //       basePrice = basePrice + (basePrice * surcharge / 100);
  //     }
  //   }

  //   return Math.round(basePrice);
  // };

  // Calculate distance first
  useEffect(() => {
    const calculateDistance = async () => {
      if (
        !formData.from ||
        (!formData.to && !["LR", "HR"].includes(formData.tripType))
      )
        return;
      if (formData.tripType === "LR") return; // no distance calc for LR
      try {
        const token = localStorage.getItem("prima_session");
        if (!token) return;

        // Filter valid stops for outstation trips - stops are already strings from autocomplete
        const validStops = ["OW", "RT"].includes(formData.tripType) && Array.isArray(formData.stops)
          ? formData.stops
            .filter(stop => {
              const stopStr = typeof stop === "string" ? stop : (stop?.name || stop?.description || "");
              return stopStr && stopStr.trim();
            })
            .map(stop => typeof stop === "string" ? stop : (stop?.name || stop?.description || ""))
          : [];

        const payload = {
          from: formData.from,
          to: formData.to || formData.from,
          tripType: formData.tripType,
          stops: validStops,
        };

        // console.log("📍 Distance calculation payload:", payload);

        const res = await axios.post(
          `${import.meta.env.VITE_API_URL}/management/calculate-distance`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        // console.log("Distance API response:", res.data);
        // API returns data nested in response.data.data
        setDistanceData(res.data.data || res.data);
      } catch (error) {
        console.error("Distance calculation error:", error);
      }
    };

    calculateDistance();
  }, [formData.from, formData.to, formData.tripType, formData.stops, isEditing]);

  // Fetch backend fare data
  useEffect(() => {
    const fetchBackendFare = async () => {
      if (!distanceData) return;

      try {
        const token = localStorage.getItem("prima_session");
        const validStops =
          ["OW", "RT"].includes(formData.tripType) &&
            Array.isArray(formData.stops)
            ? formData.stops
              .map((stop) =>
                typeof stop === "string"
                  ? stop
                  : (stop?.name || stop?.description || ""),
              )
              .map((stop) => stop.trim())
              .filter(Boolean)
            : [];

        const payload = {
          from: formData.from,
          to: formData.to || formData.from,
          tripType: formData.tripType,
          pickupDate: formData.pickupdate,
          pickupTime: formData.pickuptime,
          returnDate: formData.returndate,
          hours: formData.hours,
          distance: distanceData.distance,
          duration: distanceData.duration,
          durationText: distanceData.durationText,
          stops: validStops,
        };
        // console.log("📍 Fare calculation payload:", payload);

        const payloadKey = JSON.stringify(payload);
        //  prevent duplicate calls
        if (lastFarePayloadRef.current === payloadKey) return;
        lastFarePayloadRef.current = payloadKey;

        const res = await axios.post(
          `${import.meta.env.VITE_API_URL}/management/find`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        console.log("🟢 BACKEND FARES:", res.data.data);

        const fareMap = {};
        res.data.data.forEach((item) => {
          fareMap[item.id] = item;
        });

        setBackendFareMap(fareMap);

        // update slider max using backend price
        const prices = Object.values(fareMap).map(
          (f) => f.customerPayable || 0,
        );
        if (prices.length) {
          setMaxPrice(Math.ceil(Math.max(...prices) / 100) * 100);
        }
      } catch (err) {
        console.error("Backend fare error:", err);
      }
    };

    fetchBackendFare();
  }, [
    distanceData?.distance,
    formData.pickuptime,
    formData.hours,
    formData.pickupdate,
    formData.returndate,
    formData.tripType,
    formData.stops,
  ]);

  // Date and Time dependency validation
  useEffect(() => {
    if (formData.pickupdate && formData.pickuptime) {
      const selectedDate = parseDDMMYYYY(formData.pickupdate);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      // If selected date is today, check if time is in the past
      if (selectedDate.getTime() === today.getTime()) {
        const [hours, minutes] = formData.pickuptime.split(":").map(Number);
        const selectedTime = new Date();
        selectedTime.setHours(hours, minutes, 0, 0);

        if (selectedTime < now) {
          toast({
            title: "Invalid Time",
            description: "Please select a future time for today's date.",
            status: "warning",
            duration: 3000,
            isClosable: true,
          });
          // Don't clear the time, just warn
        }
      }
      // If selected date is in the past, show error
      else if (selectedDate < today) {
        toast({
          title: "Invalid Date",
          description: "Please select a current or future date.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        // Clear the invalid date
        setFormData((prev) => ({
          ...prev,
          pickupdate: "",
          pickuptime: "",
        }));
      }
    }
  }, [formData.pickupdate, formData.pickuptime, toast]);

  // Fetch vehicles and calculate prices
  useEffect(() => {
    const fetchVehicles = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("prima_session");
        if (!token) throw new Error("Token not found");

        const res = await axios.get(
          `${import.meta.env.VITE_API_URL}/management/vehicle-segments?page=${page}&limit=10`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        const vehicles = res.data?.data || [];
        // console.log("Fetched vehicles:", vehicles.length);
        // console.log("=== Full Vehicle Data Sample ===");
        if (vehicles.length > 0) {
          // console.log("vehicles fetched for result page:", vehicles);
          // console.log("First vehicle pricing:", vehicles[0]?.pricing);
          // console.log("First vehicle localRental:", vehicles[0]?.pricing?.localRental);
          // console.log("First vehicle hourlyRental:", vehicles[0]?.pricing?.hourlyRental);
          // console.log("First vehicle premium data:", vehicles[0]?.premium);
          // console.log("Surcharges object:", vehicles[0]?.surCharges);
          // console.log("Surcharge value:", vehicles[0]?.surCharges?.surCharge);
          // console.log("Discount object:", vehicles[0]?.discount);
          // console.log("Discount value:", vehicles[0]?.discount?.discount);
          // console.log("Premium isPremium:", vehicles[0]?.premium?.isPremium);
        }

        // Sort vehicles by creation date (latest first)
        // const sortedVehicles = vehicles.sort((a, b) => {
        //   const dateA = new Date(a.createdAt || 0);
        //   const dateB = new Date(b.createdAt || 0);
        //   return dateB - dateA; // Descending order (latest first)
        // });

        if (page === 1) setCars(vehicles);
        else setCars((prev) => [...prev, ...vehicles]);

        // Extract available hours options from vehicle data
        const hoursSet = new Set();
        vehicles.forEach((vehicle) => {
          const rentalType =
            formData.tripType === "LR" ? "localRental" : "hourlyRental";
          const rentalOptions = vehicle.pricing?.[rentalType] || [];
          rentalOptions.forEach((option) => {
            if (option.durationHours && option.includedKm && vehicle.status === "active") {
              hoursSet.add(
                JSON.stringify({
                  hours: option.durationHours,
                  km: option.includedKm,
                }),
              );
            }
          });
        });

        const hoursArray = Array.from(hoursSet)
          .map((item) => JSON.parse(item))
          .sort((a, b) => a.hours - b.hours);

        if (page === 1) setAvailableHours(hoursArray);
        // console.log("Available hours options:", hoursArray);

        setTotalPages(res.data?.totalPages || 1);

        // Calculate prices for each vehicle
        const prices = {};
        const distKm = distanceData?.distance || 0;
        // console.log("Using distance for pricing:", distKm, "km", "distanceData:", distanceData);

        // vehicles.forEach(car => {
        //   const price = calculateCarPrice(car, distKm, applySurcharges, applyDiscount);
        //   prices[car._id] = price;
        //   console.log(`Price for ${car.segmentName}:`, price);
        // });

        // setCarPrices(prev => ({ ...prev, ...prices }));

        // Calculate max price from all prices
        // const allPrices = Object.values({ ...carPrices, ...prices });
        const allPrices = Object.values({ ...prices });
        if (allPrices.length > 0) {
          const maxPriceValue = Math.max(...allPrices);
          setMaxPrice(Math.ceil(maxPriceValue / 100) * 100); // Round up to nearest 100
        }
      } catch (error) {
        console.error("Vehicle fetch error:", error);
        toast({
          title: "Error fetching vehicles",
          description: error.response?.data?.message || error.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };
    fetchVehicles();
  }, [page, distanceData]);

  // Set distance for LR based on selected hours
  useEffect(() => {
    if (formData.tripType !== "LR") return;
    if (!formData.hours) return;
    if (!availableHours.length) return;

    const selectedPackage = availableHours.find(
      (pkg) =>
        Number(pkg.hours) === Number(formData.hours) &&
        (formData.distance == null ||
          Number(pkg.km) === Number(formData.distance)),
    );

    if (!selectedPackage) return;

    // prevent infinite loop
    if (distanceData?.distance === selectedPackage.km) return;

    setDistanceData({
      distance: selectedPackage.km,
      distanceText: `${selectedPackage.km} km`,
    });
  }, [formData.tripType, formData.hours, availableHours]);

  // Recalculate prices when hours selection changes for LR/HR
  // useEffect(() => {
  //   if ((formData.tripType === "LR" || formData.tripType === "HR") && cars.length > 0) {
  //     const prices = {};
  //     const distKm = distanceData?.distance || 100;

  //     cars.forEach(car => {
  //       const price = calculateCarPrice(car, distKm, applySurcharges, applyDiscount);
  //       prices[car._id] = price;
  //     });

  //     setCarPrices(prices);

  //     // Recalculate max price
  //     const allPrices = Object.values(prices);
  //     if (allPrices.length > 0) {
  //       const maxPriceValue = Math.max(...allPrices);
  //       setMaxPrice(Math.ceil(maxPriceValue / 100) * 100);
  //     }
  //   }
  // }, [formData.hours, formData.tripType, applySurcharges, applyDiscount]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "tripType") {
      setPage(1);
      setCars([]);
      setDistanceData(null);
    }
  };

  const handleUpdate = async () => {
    console.log("=== handleUpdate called ===");
    console.log("formData before validation:", formData);

    // Reset distance and recalculate
    setDistanceData(null);
    // setCarPrices({});

    // Validation checks
    if (
      !formData.from ||
      (!formData.to && !["LR", "HR"].includes(formData.tripType))
    ) {
      console.log("Validation failed: Missing from/to", {
        from: formData.from,
        to: formData.to,
        tripType: formData.tripType,
      });
      toast({
        title: "Missing Information",
        description: "Please enter both from and to locations",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!formData.pickupdate) {
      toast({
        title: "Missing Information",
        description: "Pickup date is required",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!formData.pickuptime) {
      toast({
        title: "Missing Information",
        description: "Pickup time is required",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validation for Round Trip
    if (formData.tripType === "RT" && !formData.returndate) {
      toast({
        title: "Missing Information",
        description: "Return date is required for round trip",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validation for Oneway with stops
    if (formData.tripType === "OW" && formData.stops && formData.stops.length > 0 && !formData.returndate) {
      toast({
        title: "Missing Information",
        description: "End date is required when stops are added",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validate return date is not before pickup date
    if (formData.returndate && formData.pickupdate) {
      const pickupDate = parseDDMMYYYY(formData.pickupdate);
      const returnDate = parseDDMMYYYY(formData.returndate);
      if (returnDate < pickupDate) {
        toast({
          title: "Invalid Date",
          description: formData.tripType === "RT" ? "Return date cannot be before pickup date" : "End date cannot be before pickup date",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("prima_session");
      if (!token) return;

      // Filter valid stops for outstation trips - stops are already strings from autocomplete
      const validStops = ["OW", "RT"].includes(formData.tripType) && Array.isArray(formData.stops)
        ? formData.stops
          .filter(stop => {
            const stopStr = typeof stop === "string" ? stop : (stop?.name || stop?.description || "");
            return stopStr && stopStr.trim();
          })
          .map(stop => typeof stop === "string" ? stop : (stop?.name || stop?.description || ""))
        : [];

      const payload = {
        from: formData.from,
        to: formData.to || formData.from,
        tripType: formData.tripType,
        stops: validStops,
      };

      console.log("🔄 Recalculating distance with payload:", payload);

      const res = await axios.post(
        `${import.meta.env.VITE_API_URL}/management/calculate-distance`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // console.log("Updated Distance API response:", res.data);
      setDistanceData(res.data.data || res.data);

      toast({
        title: "Updated",
        description: `Prices recalculated with new route${validStops.length > 0 ? ` (${validStops.length} stop${validStops.length > 1 ? 's' : ''})` : ''}`,
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Distance calculation error:", error);
      toast({
        title: "Error",
        description: "Could not calculate distance",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const doesCarSupportSelectedHours = (car) => {
    if (formData.tripType !== "LR" && formData.tripType !== "HR") {
      return true; // not applicable
    }
    return !!getSelectedRentalPackage(car);
  };

  const getTripPricingForSelectedType = (car) => {
    if (formData.tripType === "OW") {
      return car.pricing?.outstationOneWay || null;
    }
    if (formData.tripType === "RT") {
      return car.pricing?.outstationRoundTrip || null;
    }
    if (formData.tripType === "AT") {
      return car.pricing?.airportTransfer || null;
    }
    if (formData.tripType === "LR" || formData.tripType === "HR") {
      return getSelectedRentalPackage(car);
    }
    return null;
  };

  const isCarPremiumForSelectedTripType = (car) => {
    const selectedTripPricing = getTripPricingForSelectedType(car);
    return selectedTripPricing?.premium?.isPremium === true;
  };

  const matchesCarTypeFilter = (car, selectedType) => {
    if (!selectedType) return true;

    const normalizedSelectedType = normalizeValue(selectedType);
    const segment = normalizeValue(car.segmentName);

    return segment === normalizedSelectedType;
  };

  const carTypeOptions = useMemo(() => {
    const activeCars = cars.filter((car) => car.status === "active");
    const countsBySegment = new Map();

    activeCars.forEach((car) => {
      const segmentName = String(car.segmentName || "").trim();
      if (!segmentName) return;
      countsBySegment.set(segmentName, (countsBySegment.get(segmentName) || 0) + 1);
    });

    return Array.from(countsBySegment.entries())
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => a.value.localeCompare(b.value));
  }, [cars]);

  // Filter vehicles based on price, type, and premium
  const filteredCars = useMemo(() => {
    return cars
      .filter((car) => {
        if (!doesCarSupportSelectedHours(car)) {
          return false;
        }
        // const carPrice = carPrices[car._id] || 0;
        const carPrice = backendFareMap[String(car._id)]?.customerPayable || 0;

        const priceMatch =
          price === 0 || price === maxPrice || carPrice <= price;
        const typeMatch = matchesCarTypeFilter(car, carTypeFilter);
        const premiumMatch = premiumServices
          ? isCarPremiumForSelectedTripType(car)
          : true;

        // Filter by surcharge: only show vehicles with isSurchargeApply = true
        // const surchargeMatch = applySurcharges
        //   ? (
        //     car.pricing?.outstationOneWay?.surcharge?.isSurchargeApply === true ||
        //     car.pricing?.outstationRoundTrip?.surcharge?.isSurchargeApply === true ||
        //     car.pricing?.airportTransfer?.surcharge?.isSurchargeApply === true
        //   )
        //   : true;

        // Filter by discount: only show vehicles with isDiscountApply = true
        // const discountMatch = applyDiscount
        //   ? (
        //     car.pricing?.outstationOneWay?.discount?.isDiscountApply === true ||
        //     car.pricing?.outstationRoundTrip?.discount?.isDiscountApply === true ||
        //     car.pricing?.airportTransfer?.discount?.isDiscountApply === true
        //   )
        //   : true;

        // Only show active vehicles
        const statusMatch = car.status === "active";

        return priceMatch && typeMatch && premiumMatch && statusMatch;
      })
      .sort((a, b) => {
        // Sort by price ascending/
        const fareA = backendFareMap[String(a._id)];
        const fareB = backendFareMap[String(b._id)];
        const customerPayableA = fareA?.customerPayable || 0;
        const gstA = fareA?.gstOnCommission || 0;
        const tollA = fareA?.tollTax || 0;
        const customerPayableB = fareB?.customerPayable || 0;
        const gstB = fareB?.gstOnCommission || 0;
        const tollB = fareB?.tollTax || 0;
        return (
          customerPayableA - gstA - tollA - (customerPayableB - gstB - tollB)
        );
      });
  }, [
    cars,
    price,
    maxPrice,
    carTypeFilter,
    premiumServices,
    backendFareMap,
    formData.tripType,
    formData.hours,
    formData.distance,
  ]);

  const validateBookingSelection = () => {
    // Validation checks before booking
    if (!formData.pickupdate) {
      toast({
        title: "Missing Information",
        description: "Pickup date is required",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    if (!formData.pickuptime) {
      toast({
        title: "Missing Information",
        description: "Pickup time is required",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    // Validation for Round Trip
    if (formData.tripType === "RT" && !formData.returndate) {
      toast({
        title: "Missing Information",
        description: "Return date is required for round trip",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    // Validation for Oneway with stops
    if (
      formData.tripType === "OW" &&
      formData.stops &&
      formData.stops.length > 0 &&
      !formData.returndate
    ) {
      toast({
        title: "Missing Information",
        description: "End date is required when stops are added",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    // Validate return date is not before pickup date
    if (formData.returndate && formData.pickupdate) {
      const pickupDate = parseDDMMYYYY(formData.pickupdate);
      const returnDate = parseDDMMYYYY(formData.returndate);
      if (returnDate < pickupDate) {
        toast({
          title: "Invalid Date",
          description:
            formData.tripType === "RT"
              ? "Return date cannot be before pickup date"
              : "End date cannot be before pickup date",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return false;
      }
    }

    return true;
  };

  const buildBookingData = (car) => {
    return {
      ...formData,
      vehicleSegmentId: String(car._id),
      distance: distanceData?.distance || 0,
      numOfDays: calculateChargedTripDays(
        formData.pickupdate,
        formData.returndate,
        formData.pickuptime,
      ),
      // estimatedPrice: carPrices[car._id] || 0,
      estimatedPrice: backendFareMap[String(car._id)]?.customerPayable || 0,
      carDetails: {
        name: car.segmentName,
        type: car.vehicleTypes,
        seats: car.passengerCapacity,
        image: car.images?.[0] || "",
        description: car.description || "",
        pricing: car.pricing || {},
        premium: car.premium || {},
      },
    };
  };

  const handleBookNow = (car) => {
    console.log("=== handleBookNow called ===");
    console.log("formData:", formData);
    console.log("car:", car);

    if (!validateBookingSelection()) {
      return;
    }

    const bookingData = buildBookingData(car);

    // Save booking data in localStorage
    localStorage.setItem("lead_booking_data", JSON.stringify(bookingData));
    localStorage.setItem("selectedVehicleId", String(car._id));
    localStorage.setItem("lead_passengers", JSON.stringify([]));

    toast({
      title: "Vehicle Selected",
      description:
        "Now you can search for existing lead or create new customer.",
      status: "success",
      duration: 2000,
      isClosable: true,
    });

    // Navigate directly to booking-details where user can search/create lead
    navigate("/bookings/lead/booking-details");
  };

  return (
    <VStack w="100%" align="stretch" p={6} bg="#eef3fb" spacing={6}>
      <Box mb={2}>
        <BreadCrumbMaker
          items={[
            { link: "/bookings", title: "Bookings" },
            { link: "/bookings/lead/search", title: "Add Booking", on: true },
            { link: "", title: "Add Leads", on: true },
          ]}
        />
      </Box>

      {/* <Text fontSize="2xl" fontWeight="bold">
        Lead/ <span style={{ color: "#2E6CF6" }}>Add Lead</span>
      </Text> */}

      {/* Form Section */}
      <Box bg="white" p={5} rounded="lg" shadow="md">
        <Flex justify="space-between" align="center" mb={4} w="full">
            <Text fontSize="lg" fontWeight="bold">Trip Details</Text>
            {isEditing ? (
              <Button colorScheme="green" size="sm" onClick={() => setIsEditing(false)}>Save Changes</Button>
            ) : (
              <Button colorScheme="blue" size="sm" onClick={() => setIsEditing(true)}>Modify Details</Button>
            )}
          </Flex>
          <Flex gap={5} flexWrap="wrap" align="flex-end">
          <Box>
            <Text fontWeight="600" mb={1}>
              Trip Type
            </Text>
            <Select name="tripType" isDisabled={!isEditing}
              w="200px"
              value={formData.tripType}
              onChange={handleInputChange}
            >
              <option value="OW">Outstation Oneway</option>
              <option value="RT">Round Trip</option>
              <option value="LR">Local Rental</option>
              <option value="AT">Airport</option>
            </Select>
          </Box>

          {formData.tripType === "AT" && (
            <Box>
              <Text fontWeight="600" mb={1}>
                Trip Section
              </Text>
              <Select name="airportTripType" isDisabled={!isEditing}
                w="180px"
                value={formData.airportTripType}
                onChange={handleInputChange}
              >
                <option value="">Select Section</option>
                <option value="PFA">Pickup From Airport</option>
                <option value="DTA">Drop At Airport</option>
              </Select>
            </Box>
          )}

          {/* From Location */}
          {["OW", "RT"].includes(formData.tripType) ? (
            <Box>
              <Text fontWeight="600" mb={1}>
                From
              </Text>
              <InputSuggestionWithExposeRef ref={fromRef} isDisabled={!isEditing}
                name="from"
                placeholder="From Location"
                value={formData.from}
                onChange={handleInputChange}
                filter="address"
                country="in"
              />
            </Box>
          ) : (
            <Box>
              <Text fontWeight="600" mb={1}>
                {formData.tripType === "AT" &&
                  (formData.airportTripType === "PFA" ||
                    formData.airportTripType === "DTA")
                  ? "From"
                  : "From"}
              </Text>
              <InputSuggestionWithExposeRef ref={fromRef} isDisabled={!isEditing}
                name="from"
                placeholder={
                  formData.tripType === "AT" &&
                    (formData.airportTripType === "PFA" ||
                      formData.airportTripType === "DTA")
                    ? "From Location"
                    : "From Location"
                }
                value={formData.from}
                onChange={handleInputChange}
                fromOrTo={
                  formData.tripType === "AT" &&
                    formData.airportTripType === "PFA"
                    ? "from"
                    : null
                }
                filter={
                  formData.tripType === "AT" &&
                    formData.airportTripType === "PFA"
                    ? "airport_with_terminal"
                    : formData.tripType === "AT" &&
                      formData.airportTripType === "DTA"
                      ? "address"
                      : undefined
                }
                airportTripType={
                  formData.tripType === "AT" ? formData.airportTripType : null
                }
                country="in"
              />
            </Box>
          )}

          {/* Add Stop Button for Outstation/RT */}
          {["OW", "RT"].includes(formData.tripType) && (!formData.stops || formData.stops.length < 5) && (
            <Button
              onClick={() => {
                if (formData.stops && formData.stops.length >= 5) {
                  toast({
                    title: "Stop limit reached",
                    description: "You can only add up to 5 stops.",
                    status: "warning",
                    duration: 3000,
                    isClosable: true,
                  });
                  return;
                }
                setFormData((prev) => ({
                  ...prev,
                  stops: [...(prev.stops || []), ""],
                }));
              }}
              size="md"
              variant="outline"
              colorScheme="blue"
              h="40px"
              mt="22px"
              leftIcon={<FiPlus />}
            >
              Add Stop
            </Button>
          )}

          {/* To/Destination */}
          {formData.tripType !== "HR" && formData.tripType !== "LR" && (
            <Box>
              <Text fontWeight="600" mb={1}>
                {formData.tripType === "AT" &&
                  (formData.airportTripType === "PFA" ||
                    formData.airportTripType === "DTA")
                  ? "To"
                  : "To"}
              </Text>
              <InputSuggestionWithExposeRef ref={toRef} isDisabled={!isEditing}
                name="to"
                placeholder={
                  formData.tripType === "AT" &&
                    (formData.airportTripType === "PFA" ||
                      formData.airportTripType === "DTA")
                    ? "To Location"
                    : "To Location"
                }
                value={formData.to}
                onChange={handleInputChange}
                fromOrTo={
                  formData.tripType === "AT" &&
                    formData.airportTripType === "DTA"
                    ? "to"
                    : null
                }
                filter={
                  formData.tripType === "AT" &&
                    formData.airportTripType === "PFA"
                    ? "address"
                    : formData.tripType === "AT" &&
                      formData.airportTripType === "DTA"
                      ? "airport"
                      : "address"
                }
                airportTripType={
                  formData.tripType === "AT" ? formData.airportTripType : null
                }
                country="in"
              />
            </Box>
          )}

          <Box w="180px">
            <Text fontWeight="600" mb={1}>
              Pickup Date
            </Text>
            <DateInput name="pickupdate" isDisabled={!isEditing}
              minDate={formatToDDMMYYYY(new Date())}
              value={formData.pickupdate}
              handleInputChange={(e) => {
                setFormData((prev) => ({
                  ...prev,
                  pickupdate: e.target.value,
                  pickuptime: "",
                }));
              }}
            />
          </Box>

          {(formData.tripType === "RT" || (formData.tripType === "OW" && formData.stops && formData.stops.length > 0)) && (
            <Box w="180px">
              <Text fontWeight="600" mb={1}>
                {formData.tripType === "RT" ? "Return Date" : "End Date"}
              </Text>
              <DateInput name="returndate" isDisabled={!isEditing}
                minDate={
                  formData.pickupdate
                    ? formData.pickupdate
                    : formatToDDMMYYYY(new Date())
                }
                value={formData.returndate}
                handleInputChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    returndate: e.target.value,
                  }));
                }}
              />
            </Box>
          )}

          <Box>
            <Text fontWeight="600" mb={1}>
              Time
            </Text>
            <Box w="180px">
              <PickupTime data={formData} isDisabled={!isEditing}
                pickupdate={formData.pickupdate}
                handleInputChange={handleInputChange}
                error={null}
              />
            </Box>
            {!formData.pickupdate && (
              <Text fontSize="sm" color="gray.500" mt={1}>
                Please select a pickup date first
              </Text>
            )}
          </Box>

          {(formData.tripType === "HR" || formData.tripType === "LR") && (
            <Box>
              <Text fontWeight="600" mb={1}>
                Hours
              </Text>

              <Flex
                border="1px solid"
                borderColor="blue.400"
                borderRadius="md"
                overflow="hidden"
                w="fit-content"
              >
                {availableHours.map((option, index) => {
                  const selectedKm = Number(
                    formData.distance ?? distanceData?.distance,
                  );
                  const isSelected =
                    Number(formData.hours) === Number(option.hours) &&
                    selectedKm === Number(option.km);

                  return (
                    <Flex
                      key={`${option.hours}-${option.km}`}
                      px={4}
                      py={2}
                      align="center"
                      justify="center"
                      cursor="pointer"
                      bg={isSelected ? "blue.500" : "white"}
                      opacity={!isEditing ? 0.6 : 1}
                      pointerEvents={!isEditing ? "none" : "auto"}
                      color={isSelected ? "white" : "gray.700"}
                      fontSize="sm"
                      fontWeight="500"
                      borderRight={
                        index !== availableHours.length - 1
                          ? "1px solid"
                          : "none"
                      }
                      borderColor="blue.600"
                      _hover={{
                        bg: isSelected ? "blue.600" : "blue.60",
                      }}
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          hours: option.hours,
                          distance: option.km,
                        }))
                      }
                    >
                      {option.hours} hrs | {option.km} km
                    </Flex>
                  );
                })}
              </Flex>
            </Box>
          )}
        </Flex>



        {/* Stops Section - Separate Row if exists */}
        {["OW", "RT"].includes(formData.tripType) &&
          formData.stops &&
          formData.stops.length > 0 && (
            <VStack align="stretch" spacing={2} mt={4}>
              {/* Route Flow Visualization */}
              {(formData.from || formData.to) && (
                <Box
                  bg="blue.50"
                  borderRadius="md"
                  p={3}
                  borderLeft="3px solid"
                  borderColor="blue.500"
                >
                  <Text fontSize="xs" fontWeight="600" color="blue.700" mb={1}>
                    Trip Route:
                  </Text>
                  <Text fontSize="sm" color="blue.800" fontWeight="500">
                    {(() => {
                      const getLocationString = (loc) => {
                        if (typeof loc === "string") return loc;
                        return loc?.name || loc?.description || loc?.address || "";
                      };

                      const truncate = (text) => {
                        if (!text) return "";
                        const firstCommaIndex = text.indexOf(",");
                        const firstWhitespaceIndex = text.indexOf(" ");

                        if (firstCommaIndex !== -1) {
                          return text.slice(0, firstCommaIndex);
                        }

                        return firstWhitespaceIndex !== -1 ? text.slice(0, firstWhitespaceIndex) : text;
                      };

                      const start = getLocationString(formData.from) || "Start";
                      const end = getLocationString(formData.to) || "Destination";
                      const hasStart = !!getLocationString(formData.from);
                      const hasEnd = !!getLocationString(formData.to);

                      const stopNames = (formData.stops || [])
                        .map((stop) => getLocationString(stop))
                        .filter((stop) => stop && stop.trim());

                      const segments = [start, ...stopNames, end];

                      if (formData.tripType === "RT" && hasStart && hasEnd) {
                        segments.push(start);
                      }

                      return segments.map((segment, index) => (
                        <Fragment key={`${segment}-${index}`}>
                          {index > 0 && (
                            <Text as="span" color="blue.600">
                              {" → "}
                            </Text>
                          )}
                          <Tooltip label={segment} hasArrow placement="top">
                            <Text as="span" cursor="help">
                              {truncate(segment)}
                            </Text>
                          </Tooltip>
                        </Fragment>
                      ));
                    })()}
                  </Text>
                </Box>
              )}

              {formData.stops.map((stop, idx) => (
                <Flex key={idx} align="flex-end" gap={2}>
                  <Box flex={0.5}>
                    <Text
                      fontWeight="600"
                      mb={1}
                      fontSize="sm"
                    >{`Stop ${idx + 1}`}</Text>
                    <InputSuggestionWithExposeRef name={`stop-${idx}`} isDisabled={!isEditing}
                      placeholder={`Enter Stop ${idx + 1}`}
                      value={
                        typeof stop === "string"
                          ? stop
                          : (stop?.name || stop?.description || "")
                      }
                      onChange={(e) => {
                        const value = e.target?.value || e;
                        const newStops = [...formData.stops];
                        newStops[idx] = value;
                        setFormData((prev) => ({
                          ...prev,
                          stops: newStops,
                        }));
                      }}
                      filter="address"
                      country="in"
                    />
                  </Box>
                  <Button
                    size="sm"
                    colorScheme="red"
                    variant="ghost"
                    h="40px"
                    w="40px"
                    minW="40px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    onClick={() => {
                      const newStops = formData.stops.filter(
                        (_, i) => i !== idx,
                      );
                      setFormData((prev) => ({
                        ...prev,
                        stops: newStops,
                      }));
                    }}
                    title="Remove Stop"
                    _hover={{ bg: "red.50" }}
                  >
                    <FiTrash2 />
                  </Button>
                </Flex>
              ))}
            </VStack>
          )}
      </Box>

      {/* Filters */}
      <Box bg="white" p={{ base: 4, md: 5 }} rounded="lg" shadow="md">
        <Flex
          direction={{ base: "column", md: "row" }}
          gap={{ base: 6, md: 10 }}
          align={{ base: "stretch", md: "center" }}
          justify="space-between"
        >
          <Flex align="center" gap={10} flexWrap="wrap">
            <Box w={{ base: "100%", md: "220px" }}>
              <Text mb={2} fontWeight="600">
                Price Range:{" "}
                {price === 0 || price === maxPrice ? "All" : `₹${price}`}
              </Text>
              <Slider
                min={0}
                max={maxPrice}
                step={100}
                value={price}
                onChange={(v) => setPrice(v)}
              >
                <SliderTrack bg="blackAlpha.400">
                  <SliderFilledTrack bg="black" />
                </SliderTrack>
                <SliderThumb />
              </Slider>
              <Flex justify="space-between" mt={1}>
                <Text fontSize="xs" color="gray.600">
                  ₹0
                </Text>
                <Text fontSize="xs" color="gray.600">
                  ₹{maxPrice}
                </Text>
              </Flex>
            </Box>

            <Box w={{ base: "100%", md: "180px" }}>
              <Text mb={2} fontWeight="600">
                Car Type
              </Text>
              <Select
                placeholder="All"
                value={carTypeFilter}
                onChange={(e) => setCarTypeFilter(e.target.value)}
              >
                {carTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {`${option.value}`}
                  </option>
                ))}
              </Select>
            </Box>

            <HStack spacing={4} wrap="wrap" align="start">
              <Checkbox onChange={(e) => setPremiumServices(e.target.checked)}>
                Premium Services
              </Checkbox>
              {/* <Checkbox onChange={(e) => setApplySurcharges(e.target.checked)}>Apply Surcharges</Checkbox>
              <Checkbox onChange={(e) => setApplyDiscount(e.target.checked)}>Apply Discount</Checkbox> */}
            </HStack>

            {distanceData?.distance > 0 &&
              formData.tripType !== "LR" &&
              formData.tripType !== "HR" && (
                <Box
                  w={{ base: "100%", md: "auto" }}
                  px={4}
                  py={3}
                  bg="blue.50"
                  borderRadius="lg"
                  borderLeft="4px solid"
                  borderColor="blue.500"
                >
                  <Text fontSize="xs" color="gray.600" mb={1}>
                    Total Distance
                  </Text>
                  <Text fontSize="lg" fontWeight="700" color="blue.700">
                    {distanceData.distanceText || `${distanceData.distance} km`}
                  </Text>
                  {((formData.tripType === "RT" && formData.pickupdate && formData.returndate) || (formData.tripType === "OW" && formData.stops?.length > 0 && formData.pickupdate && formData.returndate))
                    &&
                    (() => {
                      const days = calculateChargedTripDays(
                        formData.pickupdate,
                        formData.returndate,
                        formData.pickuptime,
                      );
                      return days > 0 ? (
                        <Text fontSize="xs" color="gray.600" mt={1}>
                          {days} {days === 1 ? "day" : "days"} trip
                        </Text>
                      ) : null;
                    })()}
                </Box>
              )}
          </Flex>
        </Flex>
      </Box>

      {/* Vehicle Cards */}
      <VStack spacing={5}>
        {loading ? (
          <Stack spacing={4} width="100%">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} height="200px" borderRadius="md" />
            ))}
          </Stack>
        ) : filteredCars.length > 0 ? (
          filteredCars.map((car) => {
            // Merge availableCars from backendFareMap
            const carWithAvailability = {
              ...car,
              availableCars: backendFareMap[String(car._id)]?.availableCars || 0,
            };
            const fare = backendFareMap[String(car._id)] || {};
            const baseFare = Number(fare?.baseFare || 0);
            const discountPercent = Number(fare?.discountPercent || 0);
            const discountAmount = Number(fare?.discountAmount || 0);
            const tollTax = Number(fare?.tollTax || 0);
            const totalCharges = Number(fare?.customerPayable || 0);
            const netBaseFare = Math.max(baseFare - discountAmount, 0);

            const tripPricing = getTripPricingForSelectedType(carWithAvailability);

            const isPremiumVehicle = isCarPremiumForSelectedTripType(carWithAvailability);

            const premiumAddons = Array.isArray(tripPricing?.premium?.addons)
              ? tripPricing.premium.addons
                .map((addon) => String(addon || "").trim())
                .filter(Boolean)
              : [];

            const amenities = Array.isArray(carWithAvailability.amenities)
              ? carWithAvailability.amenities
              : [];

            const fuelTypes = Array.isArray(carWithAvailability.fuelTypes)
              ? carWithAvailability.fuelTypes
              : [];

            // const tax = calculateTax(car, distanceData?.distance || 0);
            return (
              <Box
                key={car._id}
                w="100%"
                bg="white"
                p={{ base: 4, md: 6 }}
                rounded="lg"
                shadow="md"
                border="1px solid #e4e7eb"
              >
                <Flex
                  direction={{ base: "column", md: "row" }}
                  justify="space-between"
                  gap={{ base: 4, md: 0 }}
                >
                  <Flex
                    gap={4}
                    direction={{ base: "column", sm: "row" }}
                    align={{ base: "center", sm: "flex-start" }}
                  >
                    <Image
                      src={carWithAvailability?.images?.[0] || "https://placehold.co/150x100"}
                      w={{ base: "100%", sm: "150px" }}
                      h="100px"
                      objectFit="cover"
                      rounded="md"
                    />

                    <VStack align={{ base: "center", sm: "start" }} spacing={1}>
                      <Heading size="md">{carWithAvailability.vehicleTypes}</Heading>
                      <HStack
                        spacing={3}
                        wrap="wrap"
                        justify={{ base: "center", sm: "flex-start" }}
                      >
                        <Badge colorScheme="blue" fontSize="s">{carWithAvailability.segmentName}</Badge>
                        {isPremiumVehicle && (
                          <Badge colorScheme="purple">Premium</Badge>
                        )}
                        <HStack spacing={1}>
                          <Icon
                            as={MdAirlineSeatReclineNormal}
                            color="gray.600"
                          />
                          <Text fontSize="sm" fontWeight="500">
                            {carWithAvailability.passengerCapacity} Seats
                          </Text>
                        </HStack>
                        <HStack spacing={1}>
                          <Icon as={FaUserTie} color="green.600" />
                          <Text
                            fontSize="sm"
                            fontWeight="500"
                            color="green.600"
                          >
                            Driver 1
                          </Text>
                        </HStack>
                        {/* Available Cars Badge */}
                        <Badge
                          colorScheme={carWithAvailability.availableCars > 0 ? "green" : "red"}
                          variant="solid"
                        >
                          {carWithAvailability.availableCars || 0} Available
                        </Badge>
                      </HStack>
                      <Text fontSize="sm">{carWithAvailability.description}</Text>

                      <HStack spacing={2} wrap="wrap" justify={{ base: "center", sm: "flex-start" }}>
                        {amenities.slice(0, 2).map((amenity) => (
                          <Badge key={amenity} colorScheme="gray" variant="subtle">
                            {amenity}
                          </Badge>
                        ))}

                        {carWithAvailability?.luggageCapacity ? (
                          <Badge colorScheme="orange" variant="subtle">
                            {`Luggage: ${carWithAvailability.luggageCapacity} bags`}
                          </Badge>
                        ) : null}

                        {fuelTypes.length > 0 ? (
                          <Badge colorScheme="teal" variant="subtle">
                            {`Fuel: ${fuelTypes.slice(0, 2).join("/")}`}
                          </Badge>
                        ) : null}

                        {isPremiumVehicle ? (
                          <Badge colorScheme="purple" variant="subtle">
                            {`Addons: ${premiumAddons.length > 0
                              ? premiumAddons.slice(0, 2).join(", ")
                              : "Included"
                              }`}
                          </Badge>
                        ) : null}
                      </HStack>
                    </VStack>
                  </Flex>

                  <VStack
                    align={{ base: "center", md: "end" }}
                    textAlign={{ base: "center", md: "right" }}
                    mt={{ base: 4, md: 0 }}
                  >
                    <Box textAlign="right">
                      <Skeleton isLoaded={!loading} rounded="md" minH="60px" minW="150px" display="flex" flexDirection="column" alignItems="flex-end">
                      {baseFare > 0 && discountPercent > 0 && discountAmount > 0 && (
                        <HStack spacing={2} justify="flex-end">
                          <Text fontSize="xs" color="green.600" fontWeight="600">
                            {discountPercent}% OFF
                          </Text>
                          <Text
                            fontSize="sm"
                            color="gray.500"
                            textDecoration="line-through"
                          >
                            ₹{baseFare.toFixed(2)}
                          </Text>
                        </HStack>
                      )}

                      <Heading size="lg" color="blue.600">
                        ₹{netBaseFare.toFixed(2)}
                      </Heading>

                      {tollTax > 0 && (
                        <Text fontSize="xs" color="gray.600">
                          + <span style={{ fontStyle: "italic", color: "#666" }}>(Estimated)</span> Toll Tax: ₹{tollTax.toFixed(2)}
                        </Text>
                      )}

                      <Text fontSize="xs" color="gray.600">
                        + <span style={{ fontStyle: "italic", color: "#666" }}>Other applicable charges</span>
                      </Text>

                      <Text fontSize="xs" color="gray.600">
                        Total Charges: <span style={{ fontWeight: "bold" }}> ₹{(totalCharges || netBaseFare).toFixed(2)}</span>
                      </Text>


                      {/* Extras */}
                      {(formData.tripType === "LR" ||
                        formData.tripType === "HR") &&
                        getExtraKmRate(carWithAvailability) > 0 && (
                          <Text
                            fontSize="xs"
                            color="gray.600"
                            mt={1}
                            fontWeight="bold"
                          >
                            Extra: ₹{getExtraKmRate(carWithAvailability).toFixed(2)}/km
                          </Text>
                        )}

                      {(formData.tripType === "LR" ||
                        formData.tripType === "HR") &&
                        getExtraHourRate(carWithAvailability) > 0 && (
                          <Text
                            fontSize="xs"
                            color="gray.600"
                            mt={1}
                            fontWeight="bold"
                          >
                            Extra: ₹{getExtraHourRate(carWithAvailability).toFixed(2)}/hr
                          </Text>
                        )}
                      </Skeleton>
                    </Box>

                    <HStack
                    >
                      <Grid templateColumns={{ base: "1fr", sm: "repeat(2, auto)" }} gap={2} w="100%">
                        {(formData.tripType === "OW" || formData.tripType === "RT") && (
                          <Button
                            w={{ base: "100%", sm: "auto" }}
                            variant="outline"
                            onClick={() => {
                              if (!validateBookingSelection()) {
                                return;
                              }

                              const customBookingData = buildBookingData(
                                carWithAvailability,
                              );

                              // Save booking data to localStorage before navigating
                              localStorage.setItem("lead_booking_data", JSON.stringify(customBookingData));
                              localStorage.setItem("selectedVehicleId", String(carWithAvailability._id));
                              localStorage.setItem("lead_passengers", JSON.stringify([]));

                              // navigate("/bookings/custom-booking",
                              navigate("/bookings/lead/booking-details",
                                {
                                  state: {
                                    isCustomBooking: true,
                                    pickupLocation: formData.from,
                                    dropLocation: formData.to,
                                    pickupDate: formData.pickupdate,
                                    pickupTime: formData.pickuptime,
                                    vehicleSegmentName: carWithAvailability.segmentName,
                                  }
                                });
                            }}
                          >
                            Custom Booking
                          </Button>
                        )}
                        <Button
                          w={{ base: "100%", sm: "auto" }}
                          onClick={() => handleBookNow(carWithAvailability)}
                          colorScheme="blue"
                        >
                          Book Now
                        </Button>
                      </Grid>
                    </HStack>
                  </VStack>
                </Flex>
              </Box>
            );
          })
        ) : (
          <Text>No vehicles available</Text>
        )}

        {/* Load More button */}
        {!loading && page < totalPages && (
          <Button colorScheme="blue" onClick={() => setPage(page + 1)}>
            Load More Vehicles
          </Button>
        )}
      </VStack>
    </VStack>
  );
};

export default Results;
